import { useCallback, useRef } from 'react';
import { useSignTypedData } from '@privy-io/react-auth';
import { usePerpsWallet } from './use-perps-wallet';
import { createL1ActionHash } from '@nktkas/hyperliquid/signing';
import { useUserInfo } from '@/app/stores/useUserStore';

// 平台邀请码
const PLATFORM_REFERRAL_CODE = 'BILLIONJSMSDX';

/**
 * Hyperliquid 设置邀请码 Hook
 * 用于为新用户设置平台邀请码，享受交易手续费折扣
 */
export function useSetReferrer() {
  const { isReady } = usePerpsWallet();
  const { signTypedData } = useSignTypedData();
  const userInfo = useUserInfo();
  const isSettingRef = useRef(false);
  const hasCheckedRef = useRef(false);

  /**
   * 检查用户是否已有邀请人
   */
  const checkReferrer = useCallback(async (): Promise<boolean> => {
    if (!userInfo?.evmWalletAddress) return false;

    try {
      const response = await fetch('https://api.hyperliquid.xyz/info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'referral',
          user: userInfo.evmWalletAddress,
        }),
      });

      const data = await response.json();
      // 如果已有邀请人，返回 true
      const hasReferrer = !!data?.referredBy?.referrer;
      console.log('HL邀请码-检查结果:', { hasReferrer, data });
      return hasReferrer;
    } catch (error) {
      console.error('HL邀请码-检查失败:', error);
      return false;
    }
  }, [userInfo?.evmWalletAddress]);

  /**
   * 设置邀请码
   */
  const setReferrer = useCallback(async (code?: string): Promise<boolean> => {
    if (!isReady || !userInfo?.evmWalletAddress) {
      console.log('HL邀请码-钱包未就绪');
      return false;
    }

    if (isSettingRef.current) {
      console.log('HL邀请码-正在设置中，跳过');
      return false;
    }

    isSettingRef.current = true;
    const referralCode = code || PLATFORM_REFERRAL_CODE;

    try {
      console.log('HL邀请码-开始设置:', referralCode);
      const nonce = Date.now();

      // 1. 构造 setReferrer action
      // 注意：actionSorter 可能不支持 setReferrer，手动构造
      const action = {
        type: 'setReferrer' as const,
        code: referralCode,
      };

      console.log('HL邀请码-action:', action);

      // 2. 创建 L1 action hash
      const actionHash = createL1ActionHash({ action, nonce });
      console.log('HL邀请码-actionHash:', actionHash);

      // 3. EIP-712 签名
      const signResult = await signTypedData(
        {
          domain: {
            name: 'Exchange',
            version: '1',
            chainId: 1337, // Hyperliquid 固定 chainId
            verifyingContract: '0x0000000000000000000000000000000000000000',
          },
          types: {
            Agent: [
              { name: 'source', type: 'string' },
              { name: 'connectionId', type: 'bytes32' },
            ],
          },
          primaryType: 'Agent',
          message: {
            source: 'a', // Mainnet
            connectionId: actionHash,
          },
        },
        {
          uiOptions: {
            showWalletUIs: false, // 无感签名
          },
        }
      );

      const signatureString = signResult.signature;
      console.log('HL邀请码-签名结果:', signatureString);

      // 4. 拆分签名为 { r, s, v } 格式
      const signature = {
        r: `0x${signatureString.slice(2, 66)}` as `0x${string}`,
        s: `0x${signatureString.slice(66, 130)}` as `0x${string}`,
        v: parseInt(signatureString.slice(130, 132), 16) as 27 | 28,
      };

      // 5. 提交到 Hyperliquid API
      const response = await fetch('https://api.hyperliquid.xyz/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          signature,
          nonce,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('HL邀请码-设置结果:', result);

      if (result.status === 'ok') {
        console.log('HL邀请码-设置成功');
        return true;
      } else {
        // 常见错误：用户已有邀请人
        console.log('HL邀请码-设置失败:', result.response);
        return false;
      }
    } catch (error) {
      console.error('HL邀请码-设置异常:', error);
      return false;
    } finally {
      isSettingRef.current = false;
    }
  }, [isReady, userInfo?.evmWalletAddress, signTypedData]);

  /**
   * 检查并设置邀请码（如果用户还没有）
   * 适合在用户首次使用合约时调用
   */
  const checkAndSetReferrer = useCallback(async (): Promise<void> => {
    // 避免重复检查
    if (hasCheckedRef.current) return;
    if (!userInfo?.evmWalletAddress) return;

    hasCheckedRef.current = true;

    try {
      const hasReferrer = await checkReferrer();
      if (!hasReferrer) {
        console.log('HL邀请码-用户无邀请人，开始设置');
        await setReferrer();
      }
    } catch (error) {
      console.error('HL邀请码-检查并设置失败:', error);
    }
  }, [userInfo?.evmWalletAddress, checkReferrer, setReferrer]);

  return {
    checkReferrer,       // 检查是否已有邀请人
    setReferrer,         // 设置邀请码
    checkAndSetReferrer, // 检查并设置（推荐使用）
  };
}
